import type {
  GapCheckResult,
  NumberSequenceListResponse,
  NumberSequenceResponse,
} from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * MD-13's data layer.
 *
 * Every mutation invalidates the WHOLE key rather than patching the edited
 * row, and that is not laziness: the row's `nextCode`, `lastGiven` and status
 * are all computed by the database from the format history, and §7's Locked
 * state can have changed for a completely different sequence while the person
 * was typing. A screen that patched one row would be a screen showing stale
 * answers next to a fresh one.
 */
export const NUMBER_SEQUENCES_KEY = ['number-sequences'] as const;

export function useNumberSequences() {
  return useQuery({
    queryKey: NUMBER_SEQUENCES_KEY,
    queryFn: async (): Promise<NumberSequenceListResponse> => {
      const response = await api.numberSequences.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useUpdateNumberSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      series: string;
      scope: string;
      prefix?: string;
      padWidth?: number;
      nextNo?: number;
    }): Promise<NumberSequenceResponse> => {
      const { series, scope, ...body } = input;
      const response = await api.numberSequences.update({
        params: { series },
        query: { scope },
        body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NUMBER_SEQUENCES_KEY });
    },
  });
}

/**
 * "Check for gaps" is a MUTATION here and a GET on the wire, which looks
 * inconsistent and is deliberate: it is an action a person presses, with a
 * result they read once, not a value the screen keeps in sync. Modelling it as
 * a query would re-run it on every window focus over a table scan.
 */
export function useCheckGaps() {
  return useMutation({
    mutationFn: async (input: { series: string; scope: string }): Promise<GapCheckResult> => {
      const response = await api.numberSequences.checkGaps({
        params: { series: input.series },
        query: { scope: input.scope },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}
